import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import TableSimple from 'src/components/TableSimple';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { CustomRenderType, RowActionPropsType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import translate from '@translate';
import { ReceivingProductType, ReceivingType } from 'src/types/receiving';
import { colors, sizes } from 'src/themes/default';
import { SxProps, Theme } from '@mui/material/styles';
import productTypesWithoutVirtual from 'src/utils/productTypesWithoutVirtual';
import ProductNameLink from 'src/components/ProductNameLink';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import {
    changeReceivingProductQuantity,
    finalizeReceivingProductQuantityOnBlur,
    ProductCountInputValue,
    toProductCountInputValue,
} from './functions';
import { shouldApplyProductQuantityInput } from 'src/utils/productQuantityInput';
import { FormErrorsType } from '../../types/global';
import { flattenProductCatalogFields } from 'src/utils/productCatalogTableColumns';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import colorOptionFce from 'src/utils/colorOptionFce';

const quantityColumnStyle = { width: '1%', minWidth: '130px' };

const catalogOffColumns: TableColumnType[] = [
    { key: 'WEIGHT', label: 'weight', type: 'number-filter', defaultShow: false },
    { key: 'HS_CODE', label: 'hsCode', defaultShow: false },
    {
        key: 'AGECHECK',
        label: 'agecheck',
        type: 'select',
        selectOption: 'boolNumberCodes',
        defaultShow: false,
    },
    {
        key: 'EXP_METHOD',
        label: 'expeditionMethod',
        type: 'select',
        selectOption: 'expeditionMethodCodes',
        defaultShow: false,
    },
    {
        key: 'INSERT_TYPE',
        label: 'insertType',
        type: 'select',
        selectOption: 'productInsetTypeCodes',
        defaultShow: false,
    },
    { key: 'ROP', label: 'ROP', type: 'number-filter', defaultShow: false },
    { key: 'EXPIRATION_DAYS', label: 'EXPIRATION_DAYS', type: 'number-filter', defaultShow: false },
    {
        key: 'SCAN_IMEI_SN_TYPE',
        label: 'scanSerialNumber',
        type: 'select',
        selectOption: 'scanImeiSnCodes',
        defaultShow: false,
    },
    { key: 'SN_REGEX', label: 'snRegex', defaultShow: false },
    { key: 'WIDTH', label: 'width', type: 'number-filter', defaultShow: false },
    { key: 'HEIGHT', label: 'height', type: 'number-filter', defaultShow: false },
    { key: 'LENGTH', label: 'length', type: 'number-filter', defaultShow: false },
    { key: 'PRICE', label: 'catalogPrice', type: 'number-filter', defaultShow: false },
    {
        key: 'UNIT_PRICE',
        label: 'unitPrice',
        type: 'select',
        selectOption: 'currencyCodes',
        defaultShow: false,
    },
    {
        key: 'BATTERY_TYPE',
        label: 'batteryType',
        type: 'select',
        selectOption: 'batteryTypeCodes',
        defaultShow: false,
    },
    { key: 'CUSTOM_FIELD_1', label: 'customField1', defaultShow: false },
    { key: 'CUSTOM_FIELD_2', label: 'customField2', defaultShow: false },
    { key: 'CUSTOM_FIELD_3', label: 'customField3', defaultShow: false },
    {
        key: 'ACTIVE',
        label: 'active',
        type: 'select',
        selectOption: 'boolNumberCodes',
        defaultShow: false,
    },
    { key: 'CREATED_AT', label: 'createdAt', type: 'dateTime', defaultShow: false },
    { key: 'UPDATED_AT', label: 'updatedAt', type: 'dateTime', defaultShow: false },
    {
        key: 'IS_DELETED',
        label: 'isDeleted',
        type: 'select',
        selectOption: 'boolNumberCodes',
        defaultShow: false,
    },
    { key: 'DELETED_AT', label: 'deletedAt', type: 'dateTime', defaultShow: false },
];

const newReceivingColumns: TableColumnType[] = [
    { key: 'PRODUCT_ID', label: 'id', type: 'id', defaultShow: true },
    { key: 'SKU', label: 'sku', customRender: true, defaultShow: true },
    { key: 'NAME', label: 'name', customRender: true, defaultShow: true },
    { key: 'EAN', label: 'ean', defaultShow: true },
    {
        key: 'QUANTITY',
        label: 'quantity',
        type: 'number-filter',
        customRender: true,
        customStyle: quantityColumnStyle,
        customStyleHeader: quantityColumnStyle,
        defaultShow: true,
    },
    { key: 'LOT', label: 'lot', defaultShow: true },
    { key: 'EXPIRATION_DATE', label: 'expirationDate', type: 'date', defaultShow: true },
    { key: 'DESCRIPTION', label: 'description', defaultShow: true },
    {
        key: 'PRODUCT_TYPE',
        label: 'productType',
        type: 'select',
        selectOption: 'productTypeCodes',
        defaultShow: true,
    },
    ...catalogOffColumns,
];

const updateReceivingColumns: TableColumnType[] = [
    { key: 'PRODUCT_ID', label: 'id', type: 'id', defaultShow: true },
    { key: 'SKU', label: 'sku', customRender: true, defaultShow: true },
    { key: 'NAME', label: 'name', customRender: true, defaultShow: true },
    { key: 'EAN', label: 'ean', defaultShow: true },
    {
        key: 'QUANTITY',
        label: 'quantity',
        type: 'number-filter',
        customRender: true,
        customStyle: quantityColumnStyle,
        customStyleHeader: quantityColumnStyle,
        defaultShow: true,
    },
    { key: 'RECEIVED', label: 'receivedQuantity', type: 'number-filter', defaultShow: true },
    { key: 'DIFF_QUANTITY', label: 'differential', type: 'number-filter', defaultShow: true },
    { key: 'LOT', label: 'lot', defaultShow: true },
    { key: 'EXPIRATION_DATE', label: 'expirationDate', type: 'date', defaultShow: true },
    { key: 'DESCRIPTION', label: 'description', defaultShow: true },
    {
        key: 'PRODUCT_TYPE',
        label: 'productType',
        type: 'select',
        selectOption: 'productTypeCodes',
        defaultShow: true,
    },
    ...catalogOffColumns,
];

export interface ProductsTablePropsType {
    receivingData: ReceivingType | null;
    data?: ReceivingProductType[];
    newReceiving: boolean;
    productTypeCodes: ConstantsCodeType[];
    onChange: (value: ReceivingProductType[], error?: boolean, errorKey?: string) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
}

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const dispatch = useDispatch();
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const expeditionMethodCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes
    );
    const productInsetTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productInsetTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const batteryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.batteryTypeCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);

    const [data, setData] = useState<ReceivingProductType[]>([]);
    const [allColumns, setAllColumns] = useState<TableColumnType[]>(newReceivingColumns);

    const getInputName = (id: number, key: string) => `product-${id}-${key}`;
    const quantityOnFocusRef = useRef<Record<string, number>>({});
    const lastInputValueRef = useRef<Record<string, ProductCountInputValue>>({});

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const handleQuantityFocus = (name: string, currentQuantity: number) => {
        quantityOnFocusRef.current[name] = currentQuantity;
        lastInputValueRef.current[name] = currentQuantity;
    };

    useEffect(() => {
        if (props.newReceiving) {
            setAllColumns(newReceivingColumns);
        } else {
            setAllColumns(updateReceivingColumns);
        }
    }, [props.newReceiving]);

    useEffect(() => {
        if (props.data) {
            setData(
                props.data.map((item) => {
                    const catalog = flattenProductCatalogFields(
                        item.PRODUCT as unknown as Record<string, unknown> | undefined
                    );
                    return {
                        ...catalog,
                        RECEIVINGS_PRODUCTS_ID: item.RECEIVINGS_PRODUCTS_ID,
                        PRODUCT_ID: item.PRODUCT_ID ?? catalog.PRODUCT_ID,
                        NAME: item.NAME,
                        SKU: item.SKU,
                        QUANTITY: item.QUANTITY,
                        RECEIVED: item.RECEIVED,
                        DIFF_QUANTITY: item.DIFF_QUANTITY,
                        LOT: item.LOT,
                        EXPIRATION_DATE: item.EXPIRATION_DATE,
                        EAN: item.EAN ?? catalog.EAN,
                        DESCRIPTION: item.DESCRIPTION ?? catalog.DESCRIPTION,
                        PRODUCT_TYPE: item.PRODUCT_TYPE ?? catalog.PRODUCT_TYPE,
                        LINE_STATE: item.LINE_STATE,
                        error: item.error,
                        WEIGHT: catalog.WEIGHT,
                        HS_CODE: catalog.HS_CODE,
                        AGECHECK: catalog.AGECHECK,
                        EXP_METHOD: item.EXP_METHOD ?? catalog.EXP_METHOD,
                        INSERT_TYPE: catalog.INSERT_TYPE,
                        ROP: catalog.ROP,
                        EXPIRATION_DAYS: catalog.EXPIRATION_DAYS,
                        SCAN_IMEI_SN_TYPE: catalog.SCAN_IMEI_SN_TYPE,
                        SN_REGEX: catalog.SN_REGEX,
                        WIDTH: catalog.WIDTH,
                        HEIGHT: catalog.HEIGHT,
                        LENGTH: catalog.LENGTH,
                        PRICE: catalog.PRICE,
                        UNIT_PRICE: catalog.UNIT_PRICE,
                        BATTERY_TYPE: catalog.BATTERY_TYPE,
                        CUSTOM_FIELD_1: catalog.CUSTOM_FIELD_1,
                        CUSTOM_FIELD_2: catalog.CUSTOM_FIELD_2,
                        CUSTOM_FIELD_3: catalog.CUSTOM_FIELD_3,
                        ACTIVE: catalog.ACTIVE,
                        CREATED_AT: catalog.CREATED_AT,
                        UPDATED_AT: catalog.UPDATED_AT,
                        IS_DELETED: catalog.IS_DELETED,
                        DELETED_AT: catalog.DELETED_AT,
                        PRODUCT: item.PRODUCT,
                    };
                })
            );
        }
    }, [props.data]);

    const showDiffQuantity = props.receivingData?.STATUS === 'confirmed';

    const changeValue = (
        value: string | number | undefined | null,
        error: boolean,
        receivingsProductsId: number,
        errorKey: string
    ) => {
        const inputValue = toProductCountInputValue(value);
        lastInputValueRef.current[errorKey] = inputValue;
        if (!shouldApplyProductQuantityInput(inputValue)) {
            return;
        }
        const products = changeReceivingProductQuantity(props.data || [], receivingsProductsId, inputValue);
        props.onChange(products, error, errorKey);
    };

    const handleQuantityBlur = (receivingsProductsId: number, errorKey: string) => {
        const lastValue = lastInputValueRef.current[errorKey] ?? quantityOnFocusRef.current[errorKey] ?? 'empty';
        const quantityOnFocus = quantityOnFocusRef.current[errorKey] ?? 0;
        const products = finalizeReceivingProductQuantityOnBlur(
            props.data || [],
            receivingsProductsId,
            lastValue,
            quantityOnFocus
        );
        props.onChange(products, false, errorKey);
    };

    const handleToggleRow = (row: TableRowType) => {
        const newLineState = row.LINE_STATE === 'D' ? 'E' : 'D';

        if (row.RECEIVINGS_PRODUCTS_ID < 0) {
            if (row.LINE_STATE === 'D') {
                props.onChange(
                    props.data?.map((item) => {
                        if (item.RECEIVINGS_PRODUCTS_ID === row.RECEIVINGS_PRODUCTS_ID) {
                            return { ...item, LINE_STATE: 'E' };
                        }
                        return item;
                    }) || [],
                    false
                );
            } else {
                props.onChange(
                    props.data?.filter((item) => item.RECEIVINGS_PRODUCTS_ID != row.RECEIVINGS_PRODUCTS_ID) || [],
                    false
                );
            }
        } else {
            props.onChange(
                props.data?.map((item) => {
                    if (item.RECEIVINGS_PRODUCTS_ID === row.RECEIVINGS_PRODUCTS_ID) {
                        return { ...item, LINE_STATE: newLineState };
                    }
                    return item;
                }) || [],
                false
            );
        }
    };

    const tableSelectOptions = useMemo(
        () => ({
            productTypeCodes: [
                ...productTypesWithoutVirtual(props.productTypeCodes),
                { value: 'nonExistentSku', label: translate('receivingImport.productType.nonExistentSku') },
            ],
            expeditionMethodCodes,
            productInsetTypeCodes,
            boolNumberCodes,
            scanImeiSnCodes,
            batteryTypeCodes,
            currencyCodes,
        }),
        [
            props.productTypeCodes,
            expeditionMethodCodes,
            productInsetTypeCodes,
            boolNumberCodes,
            scanImeiSnCodes,
            batteryTypeCodes,
            currencyCodes,
        ]
    );

    const customRender: CustomRenderType = (column, row) => {
        if (column.key === 'SKU' && row.error?.some((e: { dataKey: string }) => e.dataKey === 'PRODUCT_SKU')) {
            return <span style={{ color: colors.alert_O }}>{row.SKU}</span>;
        }
        if (column.key === 'NAME') {
            return (
                <ProductNameLink
                    productId={row.PRODUCT_ID ?? row.PRODUCT?.ID}
                    name={row.NAME}
                />
            );
        }
        if (column.key === 'QUANTITY') {
            const name = getInputName(row.RECEIVINGS_PRODUCTS_ID, column.key);
            const disabledRow = row.LINE_STATE === 'D';
            const disabled = !!props.disableEdit || disabledRow;
            return (
                <TextFieldDebounced
                    value={row.QUANTITY}
                    error={props.editErrors[name]}
                    onChange={(value, inputError) =>
                        changeValue(value, inputError, row.RECEIVINGS_PRODUCTS_ID, name)
                    }
                    onFocus={() => handleQuantityFocus(name, Number(row.QUANTITY || 0))}
                    onBlur={() => handleQuantityBlur(row.RECEIVINGS_PRODUCTS_ID, name)}
                    name={name}
                    fullWidth
                    type="numberLocale"
                    min={1}
                    step="1"
                    required={!disabled}
                    disabled={disabled}
                />
            );
        }
        return undefined;
    };

    const hasInvalidSku = (r: TableRowType) => r.error?.some((e: { dataKey: string }) => e.dataKey === 'PRODUCT_SKU');
    const tableRowSxFce: (row: TableRowType) => SxProps<Theme> = (row) => {
        if (row.LINE_STATE === 'D') {
            return { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } };
        }
        if (hasInvalidSku(row)) {
            return { color: colors.alert_O, '& .MuiTableCell-root': { color: colors.alert_O } };
        }
        return {};
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <HighlightOffIcon />,
            label: translate('receivingDetail.productTable.action.removeProduct'),
            onClick: handleToggleRow,
            hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'D',
            style: { height: sizes.table_action_with_input_H },
        },
        {
            icon: <AddCircleIcon />,
            label: translate('receivingDetail.productTable.action.addProduct'),
            onClick: handleToggleRow,
            hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'E',
            style: { height: sizes.table_action_with_input_H },
        },
    ];

    const tableRows = data || [];

    return (
        <TableSimple
            rowIdKey="RECEIVINGS_PRODUCTS_ID"
            rows={tableRows}
            columsLabelPrefix="receivingDetail.productTable"
            allColumns={allColumns}
            initSort={{ column: 'RECEIVINGS_PRODUCTS_ID', direction: 'ASC', createIdNumber: true }}
            dataTestId="receiving-detail-products-table"
            userSettingsName="showReceivingDetailProductsTableColumns"
            userSettingsData={userSettingsData}
            editUserSettings={editUserSettings}
            rowActions={tableRowActions}
            selectOptions={tableSelectOptions}
            optionStyleFce={colorOptionFce}
            rowSxFce={tableRowSxFce}
            customRender={customRender}
            sumCols={
                showDiffQuantity
                    ? { QUANTITY: true, RECEIVED: true, DIFF_QUANTITY: true }
                    : { QUANTITY: true, RECEIVED: true, DIFF_QUANTITY: true }
            }
            sumColsFilter={(row) => row.LINE_STATE === 'E'}
            maxHeight={'unset'}
        />
    );
};

export default ProductsTable;
